import * as React from 'react';
import {
  Armchair,
  Smartphone,
  Refrigerator,
  Shirt,
  Dumbbell,
  Flower2,
  BookOpen,
  Blocks,
  Car,
  MoreHorizontal,
  LayoutGrid,
} from 'lucide-react';
import { cn } from '@/lib/utils/cn';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type CategoryId =
  | 'todos'
  | 'muebles'
  | 'electronica'
  | 'electrodomesticos'
  | 'ropa'
  | 'deportes'
  | 'hogar'
  | 'libros'
  | 'juguetes'
  | 'vehiculos'
  | 'otros';

interface CategoryDef {
  id: CategoryId;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Optional count of active listings */
  count?: number;
}

interface CategoryPillsProps {
  /** Currently selected category id */
  selected: CategoryId;
  /** Callback when a category is selected */
  onSelect: (id: CategoryId) => void;
  /** Per-category listing counts (optional) */
  counts?: Partial<Record<CategoryId, number>>;
  className?: string;
}

/* ------------------------------------------------------------------ */
/*  Category definitions                                               */
/* ------------------------------------------------------------------ */

const CATEGORIES: CategoryDef[] = [
  { id: 'todos', label: 'Todos', icon: LayoutGrid },
  { id: 'muebles', label: 'Muebles', icon: Armchair },
  { id: 'electronica', label: 'Electronica', icon: Smartphone },
  { id: 'electrodomesticos', label: 'Electrodomesticos', icon: Refrigerator },
  { id: 'ropa', label: 'Ropa', icon: Shirt },
  { id: 'deportes', label: 'Deportes', icon: Dumbbell },
  { id: 'hogar', label: 'Hogar', icon: Flower2 },
  { id: 'libros', label: 'Libros', icon: BookOpen },
  { id: 'juguetes', label: 'Juguetes', icon: Blocks },
  { id: 'vehiculos', label: 'Vehiculos', icon: Car },
  { id: 'otros', label: 'Otros', icon: MoreHorizontal },
];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

/**
 * CategoryPills
 * -------------
 * Horizontal scrollable row of category filter chips. Each chip
 * shows an icon + label, and optionally a listing count.
 *
 * Visual treatment:
 * - Unselected: paper-dim background, hairline border, ink-muted text
 * - Hover: paper-deep background, ink-soft text
 * - Selected: primary background, paper text (inverted, like sidebar active)
 * - Smooth scroll with hidden scrollbar on mobile
 * - Scroll fade hints on edges (CSS mask-image gradient)
 *
 * Follows the same interactive pattern as the sidebar nav items:
 * selected = bg-primary with paper text, unselected = subtle hover.
 *
 * Keyboard: tab focuses the row, arrow keys move between pills.
 */
export function CategoryPills({
  selected,
  onSelect,
  counts,
  className,
}: CategoryPillsProps) {
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  // Check scroll state
  const updateScrollState = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 4);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 4);
  }, []);

  React.useEffect(() => {
    updateScrollState();
    const el = scrollRef.current;
    if (!el) return;
    el.addEventListener('scroll', updateScrollState, { passive: true });
    const observer = new ResizeObserver(updateScrollState);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', updateScrollState);
      observer.disconnect();
    };
  }, [updateScrollState]);

  // Keyboard navigation within the pill row
  function handleKeyDown(e: React.KeyboardEvent, index: number) {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      const next = (index + 1) % CATEGORIES.length;
      const nextEl = scrollRef.current?.children[next] as HTMLElement | undefined;
      nextEl?.focus();
    }
    if (e.key === 'ArrowLeft') {
      e.preventDefault();
      const prev = (index - 1 + CATEGORIES.length) % CATEGORIES.length;
      const prevEl = scrollRef.current?.children[prev] as HTMLElement | undefined;
      prevEl?.focus();
    }
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onSelect(CATEGORIES[index]!.id);
    }
  }

  return (
    <div className={cn('relative', className)}>
      {/* Scroll fade masks */}
      {canScrollLeft && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 left-0 z-10 w-8 bg-gradient-to-r from-[var(--color-paper)] to-transparent"
        />
      )}
      {canScrollRight && (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-y-0 right-0 z-10 w-8 bg-gradient-to-l from-[var(--color-paper)] to-transparent"
        />
      )}

      {/* Scrollable row */}
      <div
        ref={scrollRef}
        role="tablist"
        aria-label="Filtrar por categoria"
        className="flex gap-2 overflow-x-auto pb-1 scrollbar-none"
      >
        {CATEGORIES.map((cat, index) => {
          const isSelected = selected === cat.id;
          const count = counts?.[cat.id];
          const Icon = cat.icon;

          return (
            <button
              key={cat.id}
              type="button"
              role="tab"
              aria-selected={isSelected}
              tabIndex={isSelected ? 0 : -1}
              onClick={() => onSelect(cat.id)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className={cn(
                'inline-flex shrink-0 items-center gap-1.5 rounded-md border px-3 py-2',
                'text-[0.8125rem] font-medium whitespace-nowrap',
                'transition-all duration-150 ease-out',
                'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--color-primary)]',
                isSelected
                  ? 'border-[var(--color-primary)] bg-[var(--color-primary)] text-[var(--color-paper)] shadow-[0_1px_0_0_rgba(0,0,0,0.1)]'
                  : 'border-[var(--color-hairline-strong)] bg-[var(--color-paper)] text-[var(--color-ink-muted)] hover:bg-[var(--color-paper-dim)] hover:text-[var(--color-ink-soft)]',
              )}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span>{cat.label}</span>
              {count !== undefined && (
                <span
                  className={cn(
                    'ml-0.5 rounded-sm px-1 py-px text-[0.625rem] font-semibold tabular',
                    isSelected
                      ? 'bg-[var(--color-paper)]/20 text-[var(--color-paper)]'
                      : 'bg-[var(--color-paper-dim)] text-[var(--color-ink-faint)]',
                  )}
                >
                  {count}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
